import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { Component, h, Event, EventEmitter, State, Prop, Listen, Host } from '@stencil/core';

import { FirebaseService, paymentMethodConverter } from '../../utils/firebase';
import { addStyleSettings } from '../../utils/custom-style';
import { customer } from '../../store/customer.store';

import { PlatformCustomerPaymentMethod } from '@artask/tambr-models/models/customer.model';


@Component({
  tag: 'tambr-payment-method-select',
  styleUrl: 'tambr-payment-method-select.css',
  shadow: true
})
export class TambrSelectPaymentMethod {

  @Prop() userId: string;
  @Prop() initialPaymentMethodId: string;
  @Prop() allowAdd: boolean = false;
  @Prop() actionLoading: boolean = false;

  @State() loading: boolean = true;
  @State() error: string | null = null;
  @State() selectedPaymentMethodId: string;

  @Event() tambrSelectPaymentMethod: EventEmitter<PlatformCustomerPaymentMethod>;
  @Event() tambrAddNewPaymentMethod: EventEmitter;
  @Event() tambrSelectPaymentMethodDialogClose: EventEmitter;

  paymentMethods: PlatformCustomerPaymentMethod[] = [];

  @Listen('click') onClickEvent( ev: MouseEvent ) {
    const targets = ev.composedPath() as EventTarget[];
    if (targets[0] instanceof Element && targets[0].id === 'tambr-payment-method-select-dialog') return this.close();
  }

  componentWillLoad() {
    this.selectedPaymentMethodId = this.initialPaymentMethodId;
  }

  async componentDidLoad() {
    this.loadCustomerPaymentMethods();
  }

  async loadCustomerPaymentMethods() {
    this.loading = true;
    try {
      const paymentMethodsCollectionQuery = query(
        collection( FirebaseService.firestore, `users/${this.userId}/paymentMethods` ),
        orderBy( 'created', 'desc' )        
      ).withConverter( paymentMethodConverter );
      const paymentMethodsCollection = await getDocs( paymentMethodsCollectionQuery );
      this.paymentMethods = paymentMethodsCollection.docs.map( doc => ({ id: doc.id, ...doc.data() }) );
      if ( this.paymentMethods.length && this.paymentMethods.findIndex( pm => pm.id == this.selectedPaymentMethodId ) === -1 ) {
        this.selectedPaymentMethodId = customer.platformCustomer.stripeDefaultPaymentMethodId ?? this.paymentMethods[0].id;
      }
    } catch(e) {
      console.error(e);
      this.error = e.message;
    }
    this.loading = false;
  }  

  selectPaymentMethod() {
    const selectedPaymentMethod = this.paymentMethods.find( (pm: PlatformCustomerPaymentMethod) => pm.id == this.selectedPaymentMethodId );
    this.tambrSelectPaymentMethod.emit( selectedPaymentMethod );
  }

  addNewPaymentMethod() { this.tambrAddNewPaymentMethod.emit(); }

  close() { this.tambrSelectPaymentMethodDialogClose.emit(); }



  render() {
    return (
      <Host style={ addStyleSettings() }>
        <div id="tambr-payment-method-select-dialog" class="tambr-overlay">
          <div class='tambr-modal-window'>

            <div class="tambr-modal-header">
              <tambr-icon type="close" class="close-button" onClick={ () => this.close() } />
            </div>

            <div class="tambr-modal-body with-padding"> 

              { this.paymentMethods.length
                ? <div class={'payment-methods-wrapper' + (this.paymentMethods.length>3 ? ' scroll' : '')}>
                    <div class="payment-methods">
                      { this.paymentMethods.map( paymentMethod => this.renderPaymentMethod(paymentMethod) )}
                    </div>
                  </div>
                : <div class="no-payment-methods">You don't have any saved payment methods yet.</div>
              }                   

              <div class="tambr-action-buttons stacked">
                <button onClick={ () => this.selectPaymentMethod() }
                  disabled={ this.loading || this.actionLoading } 
                  class={ 'tambr-button' + ((this.loading || this.actionLoading) ? ' loading' : '') }>
                    <div class="tambr-button-loading"><div>Loading...</div></div>
                    <span>Use Selected</span>
                </button>

                <div class="second-line">
                  { this.renderAddButton() }
                  <div id="dialog-cancel-button" class="tambr-clear-button" onClick={ () => this.close() }>Cancel</div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </Host>
    );
  }

  renderAddButton() {
    if ( this.allowAdd ) {
      return (<div class="tambr-clear-button" onClick={ () => this.addNewPaymentMethod() }>Add New</div>);
    }
  }

  renderPaymentMethod( paymentMethod: PlatformCustomerPaymentMethod ) {
    const type = paymentMethod.id == this.selectedPaymentMethodId ? 'radio-button-on' : 'radio-button-off';
    return (
      <div class="payment-method" onClick={() => this.selectedPaymentMethodId = paymentMethod.id}>
        <tambr-icon type={ type } class="payment-method-radio-button" />
        <tambr-payment-method paymentMethod={paymentMethod} />
      </div>
    );
  }

}
